import * as React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import DocumentUploader from './DocumentUploader';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ iconLeft, onClick, ...props }: any) => (
    <button data-icon={iconLeft} onClick={onClick} {...props} />
  ),
}));

jest.mock('components/styleguide/loading_spinner/LoadingSpinner', () => ({
  __esModule: true,
  default: () => <div data-testid="loading-spinner" role="progressbar" />,
}));

const createFile = (name = 'test.pdf', sizeInBytes = 1024, type = 'application/pdf') => {
  const file = new File(['x'.repeat(sizeInBytes)], name, { type });
  Object.defineProperty(file, 'size', { value: sizeInBytes });
  return file;
};

describe('DocumentUploader', () => {
  const mockOnFileUpload = jest.fn();
  const mockOnDocumentSelect = jest.fn();
  const mockOnPreview = jest.fn();

  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe('Accessibility - Empty state', () => {
    it('has role="button" on the dropzone', () => {
      render(<DocumentUploader />);
      expect(screen.getByRole('button')).toBeInTheDocument();
    });

    it('has an accessible label on the dropzone', () => {
      render(<DocumentUploader />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-label', 'Upload document');
    });

    it('is focusable via keyboard', () => {
      render(<DocumentUploader />);
      const dropzone = screen.getByRole('button');
      expect(dropzone).toHaveAttribute('tabIndex', '0');
    });

    it('is not focusable when disabled', () => {
      render(<DocumentUploader disabled />);
      const dropzone = screen.getByRole('button');
      expect(dropzone).toHaveAttribute('tabIndex', '-1');
    });

    it('renders hidden file input with aria-label', () => {
      render(<DocumentUploader id="upload" />);
      const input = document.querySelector('input[type="file"]');
      expect(input).toHaveAttribute('aria-label', 'Choose file to upload');
    });

    it('renders default title and body text', () => {
      render(<DocumentUploader />);
      expect(screen.getByText('Click to upload')).toBeInTheDocument();
      expect(screen.getByText('or drop it here')).toBeInTheDocument();
    });

    it('renders custom title', () => {
      render(<DocumentUploader title="Upload your CV" />);
      expect(screen.getByText('Upload your CV')).toBeInTheDocument();
    });

    it('renders subtitle when provided', () => {
      render(<DocumentUploader subtitle="PDF only. Max 10MB." />);
      expect(screen.getByText('PDF only. Max 10MB.')).toBeInTheDocument();
    });
  });

  describe('Accessibility - Loading state', () => {
    it('marks container as aria-busy when loading', () => {
      const { container } = render(<DocumentUploader isLoading />);
      expect(container.firstChild).toHaveAttribute('aria-busy', 'true');
    });

    it('has role="status" on loading wrapper', () => {
      render(<DocumentUploader isLoading />);
      expect(screen.getByRole('status')).toBeInTheDocument();
    });

    it('renders loading text', () => {
      render(<DocumentUploader isLoading loadingTitle="Uploading your resume..." />);
      expect(screen.getByText('Uploading your resume...')).toBeInTheDocument();
    });

    it('renders default loading text', () => {
      render(<DocumentUploader isLoading />);
      expect(screen.getByText('Uploading your document...')).toBeInTheDocument();
    });
  });

  describe('Accessibility - Filled state (single)', () => {
    const uploadedDoc = { id: '1', name: 'resume.pdf', size: 5242880 };

    it('displays document name and file size', () => {
      render(<DocumentUploader uploadedDocument={uploadedDoc} />);
      expect(screen.getByText('resume.pdf')).toBeInTheDocument();
      expect(screen.getByText('5.0 MB')).toBeInTheDocument();
    });

    it('has accessible label with document name', () => {
      render(<DocumentUploader uploadedDocument={uploadedDoc} />);
      expect(screen.getByRole('button', { name: 'Uploaded: resume.pdf' })).toBeInTheDocument();
    });

    it('renders replace button', () => {
      render(<DocumentUploader uploadedDocument={uploadedDoc} />);
      const repeatBtn = screen.getAllByRole('button').find(
        (btn) => btn.getAttribute('data-icon') === 'repeat'
      );
      expect(repeatBtn).toBeTruthy();
    });

    it('renders preview button when onPreview is provided', () => {
      render(<DocumentUploader uploadedDocument={uploadedDoc} onPreview={mockOnPreview} />);
      const eyeBtn = screen.getAllByRole('button').find(
        (btn) => btn.getAttribute('data-icon') === 'eye'
      );
      expect(eyeBtn).toBeTruthy();
    });

    it('does not render preview button when onPreview is absent', () => {
      render(<DocumentUploader uploadedDocument={uploadedDoc} />);
      const eyeBtn = screen.getAllByRole('button').find(
        (btn) => btn.getAttribute('data-icon') === 'eye'
      );
      expect(eyeBtn).toBeUndefined();
    });
  });

  describe('Accessibility - Filled state (multiple)', () => {
    const doc = { id: '1', name: 'resume.pdf', size: 5242880 };

    it('has aria-pressed reflecting selection state', () => {
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          isDocumentSelected={false}
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      expect(screen.getByRole('button', { name: 'Select resume.pdf' })).toHaveAttribute('aria-pressed', 'false');
    });

    it('has aria-pressed=true when selected', () => {
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          isDocumentSelected
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      expect(screen.getByRole('button', { name: 'Select resume.pdf' })).toHaveAttribute('aria-pressed', 'true');
    });

    it('renders hidden radio input with aria-label when formName is provided', () => {
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          isDocumentSelected
          formName="cv_selection"
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      const radio = screen.getByRole('radio', { hidden: true });
      expect(radio).toHaveAttribute('aria-label', 'Select resume.pdf');
    });

    it('renders radio button icon', () => {
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          isDocumentSelected={false}
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      expect(screen.getByText('', { selector: '[data-icon="radioButtonUnchecked"]' })).toBeInTheDocument();
    });

    it('renders checked radio icon when selected', () => {
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          isDocumentSelected
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      expect(screen.getByText('', { selector: '[data-icon="radioButtonChecked"]' })).toBeInTheDocument();
    });
  });

  describe('Accessibility - Hint and error', () => {
    it('renders hint with info icon', () => {
      render(<DocumentUploader hint="Helpful info" />);
      expect(screen.getByText('Helpful info')).toBeInTheDocument();
      expect(screen.getByText('', { selector: '[data-icon="info"]' })).toBeInTheDocument();
    });

    it('renders error with alert icon', () => {
      render(<DocumentUploader error="Something went wrong" />);
      expect(screen.getByText('Something went wrong')).toBeInTheDocument();
      expect(screen.getByText('', { selector: '[data-icon="triangleAlert"]' })).toBeInTheDocument();
    });

    it('hint section has aria-live="polite" for screen reader announcements', () => {
      render(<DocumentUploader hint="Info" />);
      const hintEl = screen.getByText('Info').closest('[aria-live]');
      expect(hintEl).toHaveAttribute('aria-live', 'polite');
    });

    it('prioritizes error over hint text', () => {
      render(<DocumentUploader hint="Info" error="Error occurred" />);
      expect(screen.getByText('Error occurred')).toBeInTheDocument();
      expect(screen.queryByText('Info')).not.toBeInTheDocument();
    });

    it('shows error icon (not info) when error is present', () => {
      render(<DocumentUploader hint="Info" error="Error" />);
      expect(screen.getByText('', { selector: '[data-icon="triangleAlert"]' })).toBeInTheDocument();
      expect(screen.queryByText('', { selector: '[data-icon="info"]' })).not.toBeInTheDocument();
    });
  });

  describe('Keyboard interaction', () => {
    it('opens file picker on Enter key in empty state', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const clickSpy = jest.spyOn(input, 'click');

      fireEvent.keyDown(dropzone, { key: 'Enter' });
      expect(clickSpy).toHaveBeenCalled();
    });

    it('opens file picker on Space key in empty state', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const clickSpy = jest.spyOn(input, 'click');

      fireEvent.keyDown(dropzone, { key: ' ' });
      expect(clickSpy).toHaveBeenCalled();
    });

    it('does not respond to keyboard when disabled', () => {
      render(<DocumentUploader disabled onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const clickSpy = jest.spyOn(input, 'click');

      fireEvent.keyDown(dropzone, { key: 'Enter' });
      expect(clickSpy).not.toHaveBeenCalled();
    });

    it('calls onDocumentSelect on Enter in multiple mode', () => {
      const doc = { id: '1', name: 'resume.pdf', size: 1024 };
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      const wrapper = screen.getByRole('button', { name: 'Select resume.pdf' });
      fireEvent.keyDown(wrapper, { key: 'Enter' });
      expect(mockOnDocumentSelect).toHaveBeenCalledWith(doc);
    });
  });

  describe('Click interaction', () => {
    it('opens file picker on click in empty state', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const clickSpy = jest.spyOn(input, 'click');

      fireEvent.click(dropzone);
      expect(clickSpy).toHaveBeenCalled();
    });

    it('does not open file picker when disabled', () => {
      render(<DocumentUploader disabled onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      const clickSpy = jest.spyOn(input, 'click');

      fireEvent.click(dropzone);
      expect(clickSpy).not.toHaveBeenCalled();
    });

    it('calls onDocumentSelect on click in multiple mode', () => {
      const doc = { id: '1', name: 'resume.pdf', size: 1024 };
      render(
        <DocumentUploader
          uploadedDocument={doc}
          isMultiple
          onDocumentSelect={mockOnDocumentSelect}
        />
      );
      fireEvent.click(screen.getByRole('button', { name: 'Select resume.pdf' }));
      expect(mockOnDocumentSelect).toHaveBeenCalledWith(doc);
    });
  });

  describe('File validation', () => {
    it('shows error for invalid file type', () => {
      render(<DocumentUploader accept=".pdf" onFileUpload={mockOnFileUpload} />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('image.png', 1024, 'image/png');
      fireEvent.change(input, { target: { files: [file] } });

      expect(screen.getByText('Please select a valid format and size document')).toBeInTheDocument();
      expect(mockOnFileUpload).not.toHaveBeenCalled();
    });

    it('shows error for file exceeding max size', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('large.pdf', 11 * 1024 * 1024, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });

      expect(screen.getByText('Please select a valid format and size document')).toBeInTheDocument();
      expect(mockOnFileUpload).not.toHaveBeenCalled();
    });

    it('calls onFileUpload for valid file', () => {
      render(<DocumentUploader accept=".pdf" onFileUpload={mockOnFileUpload} />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('resume.pdf', 1024, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });

      expect(mockOnFileUpload).toHaveBeenCalledWith(file);
    });

    it('shows replace error message when document already exists', () => {
      const doc = { id: '1', name: 'old.pdf', size: 1024 };
      render(
        <DocumentUploader
          uploadedDocument={doc}
          accept=".pdf"
          onFileUpload={mockOnFileUpload}
        />
      );
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('bad.png', 1024, 'image/png');
      fireEvent.change(input, { target: { files: [file] } });

      expect(screen.getByText('To replace, please select a valid format and size document')).toBeInTheDocument();
    });
  });

  describe('Form mode (name prop)', () => {
    it('stores selected file internally when name is provided', () => {
      render(<DocumentUploader name="cv[file]" accept=".pdf" />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('resume.pdf', 2048, 'application/pdf');
      fireEvent.change(input, { target: { files: [file] } });

      expect(screen.getByText('resume.pdf')).toBeInTheDocument();
      expect(screen.getByText('2.0 KB')).toBeInTheDocument();
    });

    it('sets the file input name attribute', () => {
      render(<DocumentUploader name="cv[file]" />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;
      expect(input).toHaveAttribute('name', 'cv[file]');
    });
  });

  describe('Drag and drop', () => {
    it('shows drop overlay on drag enter', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');

      fireEvent.dragEnter(dropzone);
      expect(screen.getByText('Drop it here')).toBeInTheDocument();
    });

    it('hides drop overlay on drag leave', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');

      fireEvent.dragEnter(dropzone);
      expect(screen.getByText('Drop it here')).toBeInTheDocument();

      fireEvent.dragLeave(dropzone);
      expect(screen.queryByText('Drop it here')).not.toBeInTheDocument();
    });

    it('updates aria-label during drag', () => {
      render(<DocumentUploader onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');

      fireEvent.dragEnter(dropzone);
      expect(dropzone).toHaveAttribute('aria-label', 'Drop file to upload');
    });

    it('does not activate drag when disabled', () => {
      render(<DocumentUploader disabled onFileUpload={mockOnFileUpload} />);
      const dropzone = screen.getByRole('button');

      fireEvent.dragEnter(dropzone);
      expect(screen.queryByText('Drop it here')).not.toBeInTheDocument();
    });
  });

  describe('Accessibility - aria-describedby and alert role', () => {
    it('associates hint with dropzone via aria-describedby', () => {
      render(<DocumentUploader id="upload" hint="PDF only" />);
      const dropzone = screen.getByRole('button');
      expect(dropzone).toHaveAttribute('aria-describedby', 'upload-message');
      expect(document.getElementById('upload-message')).toHaveTextContent('PDF only');
    });

    it('associates error with dropzone via aria-describedby', () => {
      render(<DocumentUploader id="upload" error="File too large" />);
      const dropzone = screen.getByRole('button');
      expect(dropzone).toHaveAttribute('aria-describedby', 'upload-message');
      expect(document.getElementById('upload-message')).toHaveTextContent('File too large');
    });

    it('does not set aria-describedby when no hint or error', () => {
      render(<DocumentUploader />);
      const dropzone = screen.getByRole('button');
      expect(dropzone).not.toHaveAttribute('aria-describedby');
    });

    it('renders error with role="alert"', () => {
      render(<DocumentUploader error="Something went wrong" />);
      expect(screen.getByRole('alert')).toHaveTextContent('Something went wrong');
    });

    it('does not render alert role for hint', () => {
      render(<DocumentUploader hint="Info" />);
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('sets aria-disabled on dropzone when disabled', () => {
      render(<DocumentUploader disabled />);
      expect(screen.getByRole('button')).toHaveAttribute('aria-disabled', 'true');
    });

    it('does not set aria-disabled when enabled', () => {
      render(<DocumentUploader />);
      expect(screen.getByRole('button')).not.toHaveAttribute('aria-disabled');
    });
  });

  describe('Error sync', () => {
    it('clears internal error when error prop changes', () => {
      const { rerender } = render(<DocumentUploader accept=".pdf" onFileUpload={mockOnFileUpload} />);
      const input = document.querySelector('input[type="file"]') as HTMLInputElement;

      const file = createFile('bad.png', 1024, 'image/png');
      fireEvent.change(input, { target: { files: [file] } });
      expect(screen.getByText('Please select a valid format and size document')).toBeInTheDocument();

      rerender(<DocumentUploader accept=".pdf" onFileUpload={mockOnFileUpload} error="Server error" />);
      expect(screen.getByText('Server error')).toBeInTheDocument();
      expect(screen.queryByText('Please select a valid format and size document')).not.toBeInTheDocument();
    });
  });
});
